import * as React from 'react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ChevronRightIcon,
  DownloadIcon,
  Loader2Icon,
  MoreVerticalIcon,
} from 'lucide-react';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

import { useContainerTypes } from '../../utils/useContainerTypes';
import { useOrderItemOptions } from '../../utils/useOrderItemOptions';
import { useStoreAssetOptions } from '../../utils/useStoreAssetOptions';
import {
  ContainerTypeDropdownContent,
  type MinimalContainerType,
} from '../table/ContainerTypeDropdown';
import type {
  ItemFormState,
  ItemKind,
  OpsOrderCarrierStatusEntry,
  OrderKind,
} from './types';
import { ItemTypeDisplay } from '@/components/ItemType/ItemTypeDisplay';
import { formatDate, formatPrice } from '@/utils/formatter';
import i18n from '@/utils/i18n';
import type { MerchantOrderTypeEnum } from '@/graphql/generatedTypes';
import { Spinner } from '@/components/ui/spinner';
import { useDownloadParcelLabel } from '../../hooks/useDownloadParcelLabel';

/**
 * ContainerType ids and store-asset ItemType ids are separate sequences that
 * both start at 1, but the dropdown identifies a row by a single number. Store
 * assets are therefore offered at an offset and decoded again on select.
 */
const STORE_ASSET_OPTION_ID_OFFSET = 1_000_000;

type DropdownOption = MinimalContainerType & {
  kind: ItemKind;
  name: string;
};

interface PackageItemsTableProps {
  items: ItemFormState[];
  orderId: string;
  storeTextId: string | null | undefined;
  orderType: MerchantOrderTypeEnum | null | undefined;
  orderKind: OrderKind;
  onAmountChange: (index: number, amount: number) => void;
  onAddItem?: (
    typeId: number,
    typeName: string,
    imageUrl: string | null,
    hexCode: string | null,
    amount: number,
  ) => void;
  onAddUntrackedItem?: (typeId: number, typeName: string) => void;
  onAddStoreAssetItem?: (
    itemTypeId: number,
    typeName: string,
    imageUrl: string | null,
    hexCode: string | null,
  ) => void;
  disabled?: boolean;
  splitLoading?: boolean;
  onSplitSelected?: (ids: string[]) => Promise<void> | void;
  onDeleteSelected?: (ids: string[]) => void;
}

export function PackageItemsTable({
  items,
  orderId,
  storeTextId,
  orderType,
  orderKind,
  onAmountChange,
  onAddItem,
  onAddUntrackedItem,
  onAddStoreAssetItem,
  disabled,
  splitLoading,
  onSplitSelected,
  onDeleteSelected,
}: PackageItemsTableProps) {
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [addPopoverOpen, setAddPopoverOpen] = useState(false);
  const [showAllContainerTypes, setShowAllContainerTypes] = useState(false);

  const containerTypes = useContainerTypes();
  const hexMap = new Map(containerTypes.map((ct) => [ct.id, ct.hexCode]));

  const { options: itemOptions, loading: itemOptionsLoading } =
    useOrderItemOptions({
      orderId,
      storeTextId,
      orderType,
      orderKind,
      includeAllContainers: showAllContainerTypes,
    });

  const { options: storeAssetOptions } = useStoreAssetOptions({ orderType });

  // Containers/untracked items are ContainerTypes, store assets are ItemTypes:
  // two independent id sequences that both start at 1, so a line is only
  // identified by kind *and* id.
  const existingKeys = new Set(items.map((i) => `${i.kind}:${i.type_id}`));

  const containerDropdownOptions: DropdownOption[] = itemOptions
    .filter(
      (opt) =>
        !existingKeys.has(`${opt.kind}:${opt.type_id}`) &&
        !(opt.disabled && !opt.disabledUntil),
    )
    .map((opt) => ({
      id: opt.type_id,
      name: opt.name,
      imageUrl: opt.imageUrl,
      hexCode: opt.hexCode,
      disabled: opt.disabled,
      disabledTooltip: opt.disabled
        ? opt.disabledUntil
          ? i18n.t('Order:itemDisabledUntil', {
              date: formatDate(opt.disabledUntil),
            })
          : i18n.t('Order:itemDisabled')
        : null,
      kind: opt.kind,
    }));

  const storeAssetDropdownOptions: DropdownOption[] = storeAssetOptions
    .filter((opt) => !existingKeys.has(`store_asset:${opt.item_type_id}`))
    .map((opt) => ({
      id: STORE_ASSET_OPTION_ID_OFFSET + opt.item_type_id,
      name: opt.name,
      imageUrl: opt.imageUrl,
      hexCode: opt.hexCode,
      disabled: false,
      disabledTooltip: null,
      kind: 'store_asset' as const,
    }));

  const dropdownOptions = [
    ...containerDropdownOptions,
    ...storeAssetDropdownOptions,
  ];

  const totalCents = items.reduce(
    (sum, item) => sum + item.amount * item.purchasePriceCents,
    0,
  );

  const isSplitDisabled =
    !onSplitSelected ||
    items.length <= 1 ||
    selectedItemIds.length === 0 ||
    Boolean(disabled) ||
    Boolean(splitLoading);

  const isDeleteDisabled =
    !onDeleteSelected || selectedItemIds.length === 0 || Boolean(disabled);

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      <div className="tw:flex tw:items-center tw:justify-between">
        {selectedItemIds.length > 0 ? (
          <span>
            {i18n.t('Order:tableItemsSelected', {
              count: selectedItemIds.length,
            })}
          </span>
        ) : (
          <div />
        )}
        <div className="tw:flex tw:items-center tw:gap-2">
          <Popover open={addPopoverOpen} onOpenChange={setAddPopoverOpen}>
            <PopoverTrigger asChild>
              <Button variant="secondary" size="sm" disabled={disabled}>
                {i18n.t('Order:addItemToOrder')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="tw:w-[380px] tw:p-0" align="end">
              <ContainerTypeDropdownContent
                containerTypes={dropdownOptions}
                onSelect={(ct) => {
                  const opt = dropdownOptions.find((o) => o.id === ct.id);
                  if (!opt || opt.disabled) return;
                  if (opt.kind === 'container') {
                    onAddItem?.(
                      opt.id,
                      opt.name,
                      opt.imageUrl ?? null,
                      opt.hexCode ?? null,
                      0,
                    );
                  } else if (opt.kind === 'untracked') {
                    onAddUntrackedItem?.(opt.id, opt.name);
                  } else {
                    onAddStoreAssetItem?.(
                      opt.id - STORE_ASSET_OPTION_ID_OFFSET,
                      opt.name,
                      opt.imageUrl ?? null,
                      opt.hexCode ?? null,
                    );
                  }
                  setAddPopoverOpen(false);
                }}
                searchPlaceholder={i18n.t('Order:filterSearchOrderItem')}
                emptyText={i18n.t('Order:filterNoOrderItems')}
              >
                <div className="tw:border-t tw:p-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="tw:w-full tw:mt-1 tw:justify-center tw:text-xs tw:font-medium tw:text-muted-foreground"
                    onClick={() => setShowAllContainerTypes((p) => !p)}
                  >
                    {!itemOptionsLoading &&
                      (showAllContainerTypes
                        ? i18n.t('Common:showStorePortfolioOnly')
                        : i18n.t('Common:showAll'))}
                    {itemOptionsLoading && <Spinner />}
                  </Button>
                </div>
              </ContainerTypeDropdownContent>
            </PopoverContent>
          </Popover>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" size="sm">
                <MoreVerticalIcon />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => onSplitSelected?.(selectedItemIds)}
                disabled={isSplitDisabled}
              >
                {i18n.t('Order:createFromSelected')}
              </DropdownMenuItem>
              <DropdownMenuItem
                variant="destructive"
                onClick={() => {
                  onDeleteSelected?.(selectedItemIds);
                  setSelectedItemIds([]);
                }}
                disabled={isDeleteDisabled}
              >
                {i18n.t('Order:deleteSelected')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {items.length === 0 ? (
        <p className="tw:text-muted-foreground tw:text-sm">
          {i18n.t('Order:tableNoItems')}
        </p>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <Checkbox
                    checked={selectedItemIds.length === items.length}
                    onCheckedChange={(checked) =>
                      setSelectedItemIds(checked ? items.map((i) => i.id) : [])
                    }
                    disabled={disabled}
                  />
                </TableHead>
                <TableHead>{i18n.t('Order:tableItemType')}</TableHead>
                <TableHead>{i18n.t('Order:tableAmount')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item, index) => (
                <TableRow
                  key={item.id}
                  className={item.disabled ? 'tw:opacity-50' : undefined}
                >
                  <TableCell>
                    <Checkbox
                      checked={selectedItemIds.includes(item.id)}
                      onCheckedChange={(checked) =>
                        setSelectedItemIds(
                          checked
                            ? [...selectedItemIds, item.id]
                            : selectedItemIds.filter((id) => id !== item.id),
                        )
                      }
                      disabled={disabled}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="tw:flex tw:items-center tw:gap-2">
                      <ItemTypeDisplay
                        alt={item.typeName}
                        hexCode={
                          item.hexCode ??
                          (item.kind === 'container'
                            ? hexMap.get(item.type_id)
                            : null) ??
                          null
                        }
                        imageUrl={item.imageUrl}
                        className="tw:size-10"
                        showLargeImageOnHover
                      />
                      <span className="tw:text-sm tw:font-medium">
                        {item.typeName}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      min={0}
                      value={item.amount}
                      onChange={(e) => {
                        const parsed = parseInt(e.target.value, 10) || 0;
                        onAmountChange(index, parsed);
                        e.target.value = String(parsed);
                      }}
                      onFocus={(e) => e.target.select()}
                      disabled={disabled || item.disabled}
                      className="tw:w-[160px]"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <p className="tw:text-sm tw:text-muted-foreground">
            {i18n.t('Order:tablePurchasePriceInfo', {
              price: formatPrice(totalCents),
            })}
          </p>
        </>
      )}
    </div>
  );
}

interface PackageWeightsTableProps {
  weights: number[];
  onWeightChange: (index: number, weight: number) => void;
  disabled?: boolean;
  errors?: Partial<Record<string, string>>;
}

export function PackageWeightsTable({
  weights,
  onWeightChange,
  disabled,
  errors,
}: PackageWeightsTableProps) {
  if (weights.length === 0) {
    return (
      <p className="tw:text-muted-foreground tw:text-sm">
        {i18n.t('Order:tableNoPackageWeights')}
      </p>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{i18n.t('Order:tablePackage')}</TableHead>
          <TableHead>{i18n.t('Order:tableWeight')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {weights.map((weight, index) => (
          <TableRow key={index}>
            <TableCell>
              {i18n.t('Order:tablePackageNumber', { n: index + 1 })}
            </TableCell>
            <TableCell>
              <div
                className="tw:flex tw:flex-col tw:gap-1"
                data-field={`packageWeight.${index}`}
              >
                <div className="tw:flex tw:items-center tw:gap-2">
                  <Input
                    type="number"
                    min={0}
                    value={weight}
                    onChange={(e) =>
                      onWeightChange(index, parseInt(e.target.value, 10) || 0)
                    }
                    disabled={disabled}
                    aria-invalid={!!errors?.[`packageWeight.${index}`]}
                    className="tw:w-[220px]"
                  />
                  <span className="tw:text-muted-foreground tw:text-sm">
                    {i18n.t('Common:kg')}
                  </span>
                </div>
                {errors?.[`packageWeight.${index}`] && (
                  <span className="tw:text-destructive tw:text-xs">
                    {errors[`packageWeight.${index}`]}
                  </span>
                )}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function TrackingCodeCell({ code }: { code: string }) {
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    if (!copied) return;
    const id = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(id);
  }, [copied]);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(code);
    setCopied(true);
  };

  return (
    <TableCell
      className="tw:font-mono tw:text-sm tw:cursor-pointer tw:select-none tw:relative tw:truncate"
      onClick={handleClick}
    >
      <span
        className={`tw:absolute tw:inset-2 tw:transition-opacity tw:duration-300 ${copied ? 'tw:opacity-0' : 'tw:opacity-100'}`}
      >
        {code}
      </span>
      <span
        className={`tw:absolute tw:inset-2 tw:transition-opacity tw:duration-300 ${copied ? 'tw:opacity-100' : 'tw:opacity-0'}`}
      >
        {i18n.t('Common:copied')}!
      </span>
    </TableCell>
  );
}

interface CarrierStatusTableProps {
  rows: OpsOrderCarrierStatusEntry[];
  orderId: string;
}

export function CarrierStatusTable({ rows, orderId }: CarrierStatusTableProps) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const { handleDownloadParcelLabel, pendingParcelId } =
    useDownloadParcelLabel(orderId);

  // Two levels of grouping. Outer: the shipment attempt, because after a
  // replacement the order's carrier history is the aggregate across every
  // attempt and a superseded parcel's rows would otherwise read as live
  // tracking. Inner: the tracking code, unchanged.
  const attemptGroups = (() => {
    const byTrackingCode = new Map<string, OpsOrderCarrierStatusEntry[]>();
    for (const row of rows) {
      const group = byTrackingCode.get(row.tracking_code);
      if (group) {
        group.push(row);
      } else {
        byTrackingCode.set(row.tracking_code, [row]);
      }
    }
    for (const group of byTrackingCode.values()) {
      group.sort((a, b) =>
        (b.updated_at ?? '').localeCompare(a.updated_at ?? ''),
      );
    }

    const byAttempt = new Map<
      number,
      {
        attemptNumber: number | null;
        isSuperseded: boolean;
        trackingCodes: Array<[string, OpsOrderCarrierStatusEntry[]]>;
      }
    >();
    for (const entry of byTrackingCode.entries()) {
      const [, entries] = entry;
      // Rows of one tracking code always belong to one attempt; -1 stands for
      // "not attributable" (legacy DPD rows, history older than attempts).
      const attemptNumber = entries[0].shipment_attempt_number ?? null;
      const key = attemptNumber ?? -1;
      const existing = byAttempt.get(key);
      if (existing) {
        existing.trackingCodes.push(entry);
      } else {
        byAttempt.set(key, {
          attemptNumber,
          isSuperseded: entries.some((row) => row.is_superseded),
          trackingCodes: [entry],
        });
      }
    }

    // Newest attempt first; unattributable rows last.
    return Array.from(byAttempt.values()).sort(
      (a, b) => (b.attemptNumber ?? -1) - (a.attemptNumber ?? -1),
    );
  })();

  if (rows.length === 0) return null;

  // The attempt the order is shipping on now: the highest numbered one that has
  // not been superseded. Ops reads the table to find out where the parcels are,
  // and "attempt 3" does not say which of the three is live.
  const currentAttemptNumber = attemptGroups.reduce<number | null>(
    (current, group) =>
      group.attemptNumber != null &&
      !group.isSuperseded &&
      (current == null || group.attemptNumber > current)
        ? group.attemptNumber
        : current,
    null,
  );

  // A single attempt is the normal case and needs no header telling ops the
  // obvious.
  const showAttemptHeaders =
    attemptGroups.length > 1 ||
    attemptGroups.some((group) => group.isSuperseded);

  const toggleExpanded = (code: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  };

  return (
    <div className="tw:rounded-md tw:border tw:p-4 tw:max-h-[400px] tw:overflow-y-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="tw:w-8" />
            <TableHead className="tw:w-[220px]">
              {i18n.t('Order:tableTrackingCode')}
            </TableHead>
            <TableHead>{i18n.t('Order:tableStatus')}</TableHead>
            <TableHead>{i18n.t('Order:tableDetails')}</TableHead>
            <TableHead>{i18n.t('Order:tableUpdatedAt')}</TableHead>
            <TableHead className="tw:w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {attemptGroups.map((group) => (
            <React.Fragment key={group.attemptNumber ?? 'unattributed'}>
              {showAttemptHeaders && (
                <TableRow className="tw:bg-muted/50 tw:hover:bg-muted/50">
                  <TableCell colSpan={6} className="tw:py-2">
                    <span className="tw:text-xs tw:font-medium tw:uppercase tw:tracking-wide tw:text-muted-foreground">
                      {(() => {
                        if (group.attemptNumber == null) {
                          return i18n.t('Order:shipmentAttemptUnknown');
                        }
                        return group.attemptNumber === currentAttemptNumber
                          ? i18n.t('Order:shipmentAttemptCurrent')
                          : i18n.t('Order:shipmentAttemptNumber', {
                              number: group.attemptNumber,
                            });
                      })()}
                    </span>
                    {group.isSuperseded && (
                      <span className="tw:ml-2 tw:rounded tw:bg-muted tw:px-1.5 tw:py-0.5 tw:text-xs tw:text-muted-foreground">
                        {i18n.t('Order:shipmentAttemptSuperseded')}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              )}
              {group.trackingCodes.map(([code, entries]) => {
                const latest = entries[0];
                const history = entries.slice(1);
                const isExpanded = expanded.has(code);
                const hasHistory = history.length > 0;
                const mutedClass = group.isSuperseded
                  ? 'tw:text-muted-foreground'
                  : '';

                return (
                  <React.Fragment key={code}>
                    <TableRow
                      className={[
                        hasHistory ? 'tw:cursor-pointer' : '',
                        mutedClass,
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={
                        hasHistory ? () => toggleExpanded(code) : undefined
                      }
                    >
                      <TableCell className="tw:w-8 tw:px-2">
                        {hasHistory && (
                          <ChevronRightIcon
                            className={`tw:size-4 tw:text-muted-foreground tw:transition-transform tw:duration-200 ${isExpanded ? 'tw:rotate-90' : ''}`}
                          />
                        )}
                      </TableCell>
                      <TrackingCodeCell code={latest.tracking_code} />
                      <TableCell>
                        <span className="tw:flex tw:items-center tw:gap-2">
                          {latest.status || '--'}
                          {/* A partial replacement leaves replaced and
                              carried-over parcels on the same live attempt, so
                              the attempt header no longer says which parcels
                              are the new ones. */}
                          {latest.is_replacement && (
                            <span className="tw:rounded tw:bg-primary/10 tw:px-1.5 tw:py-0.5 tw:text-xs tw:font-medium tw:text-primary">
                              {i18n.t('Order:parcelIsReplacement')}
                            </span>
                          )}
                        </span>
                      </TableCell>
                      <TableCell>{latest.status_details || '--'}</TableCell>
                      <TableCell>{latest.updated_at || '--'}</TableCell>
                      <TableCell className="tw:w-10 tw:px-2">
                        {latest.parcel_id && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="tw:size-7"
                            title={i18n.t('Order:downloadParcelLabel')}
                            disabled={pendingParcelId === latest.parcel_id}
                            onClick={(e) => {
                              // The row itself toggles the history.
                              e.stopPropagation();
                              handleDownloadParcelLabel(latest.parcel_id!);
                            }}
                          >
                            {pendingParcelId === latest.parcel_id ? (
                              <Loader2Icon className="tw:size-4 tw:animate-spin" />
                            ) : (
                              <DownloadIcon className="tw:size-4" />
                            )}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                    {isExpanded &&
                      history.map((row, idx) => (
                        <TableRow
                          key={`${code}-history-${idx}`}
                          className="tw:text-muted-foreground tw:bg-muted/30"
                        >
                          <TableCell className="tw:w-8 tw:px-2" />
                          <TableCell />
                          <TableCell>{row.status || '--'}</TableCell>
                          <TableCell>{row.status_details || '--'}</TableCell>
                          <TableCell>{row.updated_at || '--'}</TableCell>
                          <TableCell className="tw:w-10 tw:px-2" />
                        </TableRow>
                      ))}
                  </React.Fragment>
                );
              })}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
